import React, { useEffect, useState } from "react";
import {Box,Grid,Card,CardContent,Typography,Button,LinearProgress,Chip,Avatar,CircularProgress,Alert,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import {MenuBook,TrendingUp,LocalFireDepartment,PlayArrow,CheckCircle,ArrowForward,School,Quiz as QuizIcon,
} from "@mui/icons-material";

import ModuleIcon from "./moduleIcons";
import { getModules } from "./englishApi";

const PRIMARY = "#006D77";
const SECONDARY = "#00A8B5";
const PAGE_BG = "#F4F6F8";
const BORDER = "#E0F2F1";

export default function EnglishDashboard() {
  const navigate = useNavigate();
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    getModules()
      .then((data) => setModules(data))
      .catch(() => setError("Couldn't load the English course right now. Please try again."))
      .finally(() => setLoading(false));
  }, []);

  const totalTutorials = modules.reduce((sum, m) => sum + (m.tutorial_count || 0), 0);
  const completedTutorials = modules.reduce((sum, m) => sum + (m.completed_tutorials || 0), 0);
  const overallProgress = totalTutorials
    ? Math.round((completedTutorials / totalTutorials) * 100)
    : 0;

  const currentModule =
    modules.find((m) => m.progress_percent > 0 && m.progress_percent < 100) || modules[0];

  const openModule = (moduleId) => navigate(`/english/${moduleId}`);


const DRAWER_WIDTH = 280;
if (loading) {
  return (
    <Box sx={{ display: "flex", justifyContent: "center", p: 8, ml: { xs: 0, md: `${DRAWER_WIDTH}px` } }}>
      <CircularProgress sx={{ color: PRIMARY }} />
    </Box>
  );
}

if (error) {
  return (
    <Box sx={{ p: 3, ml: { xs: 0, md: `${DRAWER_WIDTH}px` } }}>
      <Alert severity="error">{error}</Alert>
    </Box>
  );
}
  return (
     <Box sx={{ bgcolor: PAGE_BG, minHeight: "100vh", p: { xs: 2, md: 3 }, ml: { xs: 0, md: `${DRAWER_WIDTH}px` } }}>
      {/* HEADER */}
      <Box
        sx={{
          minHeight: 160,
          borderRadius: 3,
          mb: 3,
          p: { xs: 3, md: 4 },
          color: "white",
          position: "relative",
          overflow: "hidden",
          background:
            "linear-gradient(135deg, rgba(0,109,119,0.96) 0%, rgba(0,168,181,0.82) 100%)",
          boxShadow: "0 8px 24px rgba(0,109,119,0.15)",
        }}
      >
        <Box sx={{ position: "relative", zIndex: 2 }}>
          <Typography variant="h4" fontWeight={700} sx={{ fontSize: { xs: "1.8rem", md: "2.2rem" }, mb: 1 }}>
            English Learning
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.92, maxWidth: 650 }}>
            Improve your English step by step with lessons, vocabulary, and practice quizzes.
          </Typography>
          {currentModule && (
            <Button
              variant="contained"
              startIcon={<PlayArrow />}
              onClick={() => openModule(currentModule.id)}
              sx={{ mt: 3, bgcolor: "white", color: PRIMARY, fontWeight: 600, "&:hover": { bgcolor: "#F2FFFF" } }}
            >
              Continue Learning
            </Button>
          )}
        </Box>
        <School
          sx={{
            position: "absolute",
            right: { xs: 20, md: 70 },
            bottom: -10,
            fontSize: { xs: 70, md: 110 },
            opacity: 0.16,
          }}
        />
      </Box>

      {/* STATS */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard icon={<MenuBook />} title="Modules" value={modules.length} subtitle="Learning modules" />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            icon={<TrendingUp />}
            title="Progress"
            value={`${overallProgress}%`}
            subtitle="Overall completion"
            progress={overallProgress}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            icon={<LocalFireDepartment />}
            title="Tutorials Completed"
            value={`${completedTutorials}/${totalTutorials}`}
            subtitle="Keep learning!"
          />
        </Grid>
      </Grid>

      {/* MODULES */}
      <Card sx={{ borderRadius: 3, border: `1px solid ${BORDER}`, boxShadow: "none" }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" fontWeight={700}>
              Learning Modules
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Follow the modules in order to build your English skills.
            </Typography>
          </Box>

          <Grid container spacing={2.5}>
            {modules.map((module) => (
              <Grid item xs={12} sm={6} md={4} key={module.id}>
                <ModuleCard module={module} onOpen={() => openModule(module.id)} />
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
}

function StatCard({ icon, title, value, subtitle, progress }) {
  return (
    <Card sx={{ borderRadius: 3, border: `1px solid ${BORDER}`, boxShadow: "none", height: "100%" }}>
      <CardContent>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <Avatar sx={{ bgcolor: "#E0F2F1", color: PRIMARY }}>{icon}</Avatar>
          {progress !== undefined && (
            <Chip label={`${progress}%`} size="small" sx={{ bgcolor: "#E0F2F1", color: PRIMARY, fontWeight: 700 }} />
          )}
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          {title}
        </Typography>
        <Typography variant="h5" fontWeight={700} sx={{ color: PRIMARY, mt: 0.3 }}>
          {value}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {subtitle}
        </Typography>
        {progress !== undefined && (
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              mt: 1.5,
              height: 6,
              borderRadius: 5,
              bgcolor: "#D9EEEE",
              "& .MuiLinearProgress-bar": { bgcolor: SECONDARY, borderRadius: 5 },
            }}
          />
        )}
      </CardContent>
    </Card>
  );
}

function ModuleCard({ module, onOpen }) {
  const progress = module.progress_percent || 0;
  const isCompleted = progress === 100;
  const hasQuiz = module.quiz_count > 0;

  return (
    <Card
      sx={{
        height: "100%",
        borderRadius: 3,
        border: `1px solid ${BORDER}`,
        boxShadow: "none",
        transition: "0.2s",
        "&:hover": { transform: "translateY(-4px)", boxShadow: "0 8px 20px rgba(0,109,119,0.12)" },
      }}
    >
      <CardContent sx={{ p: 2.5 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
          <Avatar sx={{ width: 52, height: 52, bgcolor: "#E0F2F1", color: PRIMARY }}>
            <ModuleIcon name={module.icon} />
          </Avatar>
          <Chip
            size="small"
            icon={isCompleted ? <CheckCircle /> : undefined}
            label={isCompleted ? "Completed" : progress > 0 ? "In Progress" : "Not Started"}
            sx={{
              bgcolor: isCompleted ? "#E8F5E9" : "#E0F2F1",
              color: isCompleted ? "#2E7D32" : PRIMARY,
              fontWeight: 600,
            }}
          />
        </Box>

        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
          {module.title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ minHeight: 42, mb: 2 }}>
          {module.subtitle}
        </Typography>

        <Box sx={{ mb: 1 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              {module.completed_tutorials || 0} / {module.tutorial_count} tutorials
            </Typography>
            <Typography variant="caption" fontWeight={700} color={PRIMARY}>
              {progress}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: 6,
              borderRadius: 5,
              bgcolor: "#E8EEEE",
              "& .MuiLinearProgress-bar": { bgcolor: isCompleted ? "#43A047" : SECONDARY, borderRadius: 5 },
            }}
          />
        </Box>

        <Box sx={{ display: "flex", gap: 1, mt: 1.5 }}>
          <Button
            fullWidth
            variant={isCompleted ? "outlined" : "contained"}
            endIcon={<ArrowForward />}
            onClick={onOpen}
            sx={
              isCompleted
                ? { borderColor: PRIMARY, color: PRIMARY }
                : { background: "linear-gradient(135deg, #006D77 0%, #00A8B5 100%)" }
            }
          >
            {isCompleted ? "Review" : progress > 0 ? "Continue" : "Start"}
          </Button>
          {hasQuiz && (
            <Button
              variant="outlined"
              sx={{ minWidth: 44, px: 1.2, borderColor: BORDER, color: PRIMARY }}
              onClick={() => onOpen()}
              title="Practice quiz available"
            >
              <QuizIcon fontSize="small" />
            </Button>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}
